// src/router/index.js

import { createRouter, createWebHistory } from 'vue-router';
import store from '../store';
import { i18n, setI18nLocale } from '../i18n';

const t = (key, fallback) => {
  try {
    const v = i18n.global.t(key);
    return v === key ? fallback : v;
  } catch { return fallback; }
};
const title = (key, fallback) => `iSamurai - ${t(key, fallback)}`;

const routes = [
  {
    path: '/',
    name: 'Home',
    // Home2 promoted to the main home (Tarik 2026-07-09); legacy Home.vue kept on
    // disk for rollback — restore by pointing this import back at it.
    component: () => import('../views/Home2.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.home', titleFallback: 'Home Page' },
  },
  {
    // side-by-side navbar redesign DRAFT (renders Navbar2 via App.vue) — preview only
    path: '/home2',
    name: 'Home2Preview',
    component: () => import('../views/Home2Preview.vue'),
    meta: { requiresAuth: true, titleFallback: 'Home (navbar draft)' },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { titleKey: 'routes.login', titleFallback: 'Login' },
  },
  {
    path: '/faceswap',
    name: 'FaceSwap',
    component: () => import('../views/FaceSwap.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.faceswap', titleFallback: 'Face Swap' },
  },
  {
    path: '/faceswap2',
    name: 'FaceSwap2',
    component: () => import('../views/FaceSwap2.vue'),
    meta: { requiresAuth: true, titleFallback: 'Face Swap 2.0 (test)' },
  },
  {
    path: '/faceswap-v1',
    name: 'FaceSwapV1',
    component: () => import('../views/FaceSwapV1.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.faceswap', titleFallback: 'Face Swap' },
  },
  {
    path: '/multi-faceswap-v1',
    name: 'MultiFaceSwapV1',
    component: () => import('../views/MultiFaceSwapV1.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.faceswap', titleFallback: 'Multi Face Swap' },
  },
  {
    path: '/studio',
    name: 'Studio',
    component: () => import('../views/Studio.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.studio', titleFallback: 'Video Editor Studio' },
  },
  {
    path: '/gallery',
    name: 'Gallery',
    component: () => import('../views/Gallery.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.gallery', titleFallback: 'Gallery' },
  },
  {
    path: '/facerestore',
    name: 'FaceRestore',
    component: () => import('../views/FaceRestore.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.facerestore', titleFallback: 'Face Restore' },
  },
  {
    path: '/image-restore',
    name: 'ImageRestore',
    component: () => import('../views/ImageRestore.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.imageRestore', titleFallback: 'Image Restore' },
  },
  {
    // 2026-08-13: Imagine Studio now serves the NEW studio (ImagineStudio2.vue) —
    // modes Create / Head Swap / Scene, steps-based pricing, prompt helper.
    // Rollback = point this import back at ImagineStudio.vue (kept on disk).
    path: '/imagine-studio',
    name: 'ImagineStudio',
    component: () => import('../views/ImagineStudio2.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.imagineStudio', titleFallback: 'Imagine Studio' },
  },
  // the old engine-backed studio, kept reachable for one-click comparison/rollback
  {
    path: '/imagine-studio-legacy',
    name: 'ImagineStudioLegacy',
    component: () => import('../views/ImagineStudio.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.imagineStudio', titleFallback: 'Imagine Studio (legacy)' },
  },
  { path: '/imagine-studio-2', redirect: '/imagine-studio' },
  {
    // Canonical head-swap URL is now /headswap; keep /headswap-2 as a redirect
    // so any old links / bookmarks still land on the live page.
    path: '/headswap-2',
    redirect: '/headswap',
  },
  // Head Swap & Character Swap are DOWN FOR MAINTENANCE — routes redirect to
  // home, where clicking their cards shows a maintenance modal. (Components
  // kept in the repo; restore the routes above to bring them back.)
  {
    path: '/headswap',
    name: 'HeadSwapStudio',
    component: () => import('../views/HeadSwapStudio2.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.headSwap', titleFallback: 'Head Swap Studio', modes: ['headswap'] },
  },
  {
    path: '/character-animate',
    name: 'CharacterAnimate',
    component: () => import('../views/HeadSwapStudio2.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.headSwap', titleFallback: 'Character Swap & Animate Image', modes: ['charswap', 'animate'] },
  },
  {
    path: '/slow-motion',
    name: 'SlowMotion',
    component: () => import('../views/SlowMotion.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.slowMotion', titleFallback: 'Slow Motion Studio' },
  },
  {
    path: '/video-editor',
    name: 'VideoEditor',
    component: () => import('../views/VideoEditor.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.videoEditor', titleFallback: 'Video Editor' },
  },
  {
    path: '/subscriptions',
    name: 'Subscription',
    component: () => import('../views/Subscription.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.subscription', titleFallback: 'Store & Subscriptions' },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.profile', titleFallback: 'Profile' },
  },
  {
    path: '/support-center',
    name: 'SupportCenter',
    component: () => import('../views/SupportCenter.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.support', titleFallback: 'Support Center' },
  },

  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue'),
    meta: { titleKey: 'routes.register', titleFallback: 'Sign up' },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { titleKey: 'routes.resetPassword', titleFallback: 'Reset password' },
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('../views/VerifyEmail.vue'),
    meta: { titleKey: 'routes.verifyEmail', titleFallback: 'Verify email' },
  },

  // Backwards-compat redirects from legacy URLs
  { path: '/gallery2', redirect: '/gallery' },
  { path: '/faceswapstudio2', redirect: '/studio' },
  { path: '/face-swap-studio', redirect: '/studio' },
  { path: '/multi-faceswap', redirect: '/faceswap' },

  // 404 catch-all
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || '/cockpit/'),   // follows publicPath (build-time base)
  routes,
});

// A deploy replaces the hashed chunks; a tab opened BEFORE the deploy fails its
// next lazy route import (white screen until a manual refresh). Reload once to
// pick up the fresh index.html — guarded so a real outage can't reload-loop.
router.onError((err) => {
  const msg = String((err && err.message) || err);
  if (/loading chunk .*failed|loading css chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(msg)) {
    let last = 0;
    try { last = Number(sessionStorage.getItem('chunk_reload_ts') || 0); } catch (e) { /* private mode */ }
    if (Date.now() - last > 15000) {
      try { sessionStorage.setItem('chunk_reload_ts', String(Date.now())); } catch (e) { /* private mode */ }
      window.location.reload();
    }
  }
});

router.beforeEach(async (to, from, next) => {
  await setI18nLocale().catch(() => {});
  if (to.meta.titleKey) {
    document.title = title(to.meta.titleKey, to.meta.titleFallback || '');
  } else if (to.meta.title) {
    document.title = to.meta.title;
  } else {
    document.title = 'iSamurai';
  }

  const loggedIn = store.getters.isLoggedIn;

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!loggedIn) {
      next('/login');
    } else {
      next();
    }
  } else if (to.name === 'Login' && loggedIn) {
    // OAuth handoff carries a FRESH token in the query. A stale localStorage
    // session must not bounce us to '/' here — that drops the token and strands
    // the user in a 401 loop (google-login bug, 2026-07-09). Let Login consume it.
    if (to.query && to.query.token) next();
    else next('/');
  } else {
    next();
  }
});

router.afterEach(() => {});

export default router;
